/**
 * Permission Utility Functions
 *
 * This file provides utility functions for checking permissions and managing drafts.
 */

import { Permission, EntityType, DraftType, type UserPermissions } from '@lib/permissions';

// ============================================================================
// PERMISSION CHECKING
// ============================================================================

/**
 * Check if user has a specific permission
 * Checks both direct permissions and role-based permissions
 */
export function hasPermission(
  userPermissions: UserPermissions | null | undefined,
  permission: Permission | string
): boolean {
  if (!userPermissions) return false;
  return userPermissions.permissions?.includes(permission) ?? false;
}

/**
 * Check if user has ANY of the specified permissions
 */
export function hasAnyPermission(
  userPermissions: UserPermissions | null | undefined,
  permissions: (Permission | string)[]
): boolean {
  if (!userPermissions) return false;
  return permissions.some((permission) =>
    userPermissions.permissions?.includes(permission)
  );
}

/**
 * Check if user has ALL of the specified permissions
 */
export function hasAllPermissions(
  userPermissions: UserPermissions | null | undefined,
  permissions: (Permission | string)[]
): boolean {
  if (!userPermissions) return false;
  return permissions.every((permission) =>
    userPermissions.permissions?.includes(permission)
  );
}

/**
 * Check if user is an admin (identified by ADMIN_MANAGE_PERMISSIONS)
 */
export function isAdmin(userPermissions: UserPermissions | null | undefined): boolean {
  return hasPermission(userPermissions, Permission.ADMIN_MANAGE_PERMISSIONS);
}

// ============================================================================
// ENTITY UPDATE PERMISSIONS
// ============================================================================

/**
 * Check if user can update an entity directly (without creating a draft)
 */
export function canUpdateDirectly(
  userPermissions: UserPermissions | null | undefined,
  entityType: EntityType,
  entityOwnerId?: string,
  currentUserId?: string
): boolean {
  if (!userPermissions) return false;

  if (isAdmin(userPermissions)) return true;

  // Map entity type to permissions
  // Note: alternates array allows for multiple permissions that grant the same access
  const permissionMap: Record<EntityType, { all: Permission; own?: Permission; alternates?: Permission[] }> = {
    [EntityType.USER]: {
      all: Permission.USERS_UPDATE_ALL,
      own: Permission.USERS_UPDATE_OWN,
    },
    [EntityType.CONTENT]: {
      all: Permission.CONTENT_UPDATE_ALL,
      own: Permission.CONTENT_UPDATE_OWN,
    },
    [EntityType.TEAM]: {
      all: Permission.TEAMS_UPDATE_ALL,
      own: Permission.TEAMS_UPDATE_OWN,
    },
    [EntityType.SPONSOR]: {
      all: Permission.SPONSORS_UPDATE_ALL,
    },
    [EntityType.TRAINER]: {
      all: Permission.TRAINERS_UPDATE_ALL,
      own: Permission.TRAINERS_UPDATE_OWN,
    },
    [EntityType.BOARD]: {
      all: Permission.BOARD_UPDATE_ALL,
    },
    // Ownership for matches is team-based (trainer of the team), see canEditMatch
    [EntityType.MATCH]: {
      all: Permission.MATCHES_UPDATE_ALL,
    },
  };

  const permissions = permissionMap[entityType];

  // Check if user has full update permission (primary or alternates)
  if (hasPermission(userPermissions, permissions.all)) {
    return true;
  }

  // Check alternate permissions
  if (permissions.alternates) {
    for (const altPermission of permissions.alternates) {
      if (hasPermission(userPermissions, altPermission)) {
        return true;
      }
    }
  }

  // Check if user owns the entity and has own update permission
  if (
    permissions.own &&
    entityOwnerId &&
    currentUserId &&
    entityOwnerId === currentUserId &&
    hasPermission(userPermissions, permissions.own)
  ) {
    return true;
  }

  return false;
}

/**
 * Check if user can create a draft update
 *
 * Note: With the new inline draft system, users who can view and edit entities
 * can create drafts. The publishing permission determines if changes go live immediately.
 */
export function canDraftUpdate(
  userPermissions: UserPermissions | null | undefined,
  entityType: EntityType
): boolean {
  if (!userPermissions) return false;

  // Admin can always draft (though they probably won't need to)
  if (isAdmin(userPermissions)) return true;

  // With the new system, anyone who can view and update (even "own") can create drafts
  // The difference is whether they have publishing permissions or not
  return canUpdateDirectly(userPermissions, entityType);
}

/**
 * Check if user can publish (review/approve) drafts for an entity type
 *
 * Note: With the new inline draft system, publishing permissions have replaced
 * the old review permissions. Only manager roles should have publishing permissions.
 */
export function canReviewDrafts(
  userPermissions: UserPermissions | null | undefined,
  entityType: EntityType
): boolean {
  if (!userPermissions) return false;

  // Admin can always publish
  if (isAdmin(userPermissions)) return true;

  // Map entity types to their publishing permissions
  const publishPermissionMap: Record<EntityType, Permission | null> = {
    [EntityType.CONTENT]: Permission.CONTENT_PUBLISH,
    [EntityType.TEAM]: Permission.TEAMS_UPDATE_ALL, // Team managers can publish
    [EntityType.SPONSOR]: Permission.SPONSORS_UPDATE_ALL, // Sponsor managers can publish
    [EntityType.USER]: Permission.USERS_UPDATE_ALL, // User managers can publish
    [EntityType.TRAINER]: Permission.TRAINERS_UPDATE_ALL, // User managers can publish trainer updates
    [EntityType.BOARD]: Permission.BOARD_UPDATE_ALL, // User managers can publish board updates
    [EntityType.MATCH]: Permission.MATCHES_UPDATE_ALL, // Match edits always go live directly
  };

  const permission = publishPermissionMap[entityType];
  if (!permission) return false;

  return hasPermission(userPermissions, permission);
}

/**
 * Determine the update strategy for an entity
 * Returns: 'direct' | 'draft' | 'none'
 *
 * Note: With the new inline draft system:
 * - 'direct' means user has publishing permissions (changes go live immediately)
 * - 'draft' means user can edit but changes require approval (manager review)
 * - 'none' means user cannot make changes at all
 */
export function getUpdateStrategy(
  userPermissions: UserPermissions | null | undefined,
  entityType: EntityType,
  entityOwnerId?: string,
  currentUserId?: string
): 'direct' | 'draft' | 'none' {
  if (!userPermissions) return 'none';

  // Check if user has publishing permissions (can update and publish)
  const canPublish = canReviewDrafts(userPermissions, entityType);
  const canUpdate = canUpdateDirectly(userPermissions, entityType, entityOwnerId, currentUserId);

  if (canPublish && canUpdate) {
    return 'direct'; // Manager role - changes go live immediately
  }

  if (canUpdate) {
    return 'draft'; // Can edit but needs approval (e.g., TRAINER, CONTENT_EDITOR)
  }

  // User cannot update at all
  return 'none';
}

// ============================================================================
// ENTITY CREATION PERMISSIONS
// ============================================================================

/**
 * Check if user can create an entity
 */
export function canCreate(
  userPermissions: UserPermissions | null | undefined,
  entityType: EntityType,
  userId?: string,
  currentUserId?: string
): boolean {
  if (!userPermissions) return false;

  // Admin can always create
  if (isAdmin(userPermissions)) return true;

  const createPermissionMap: Record<EntityType, { all: Permission; own?: Permission }> = {
    [EntityType.USER]: {
      all: Permission.USERS_CREATE_USER,
    },
    [EntityType.CONTENT]: {
      all: Permission.CONTENT_CREATE_PUBLISHED,
    },
    [EntityType.TEAM]: {
      all: Permission.TEAMS_CREATE,
    },
    [EntityType.SPONSOR]: {
      all: Permission.SPONSORS_CREATE,
    },
    [EntityType.TRAINER]: {
      all: Permission.TRAINERS_CREATE,
      own: Permission.TRAINERS_CREATE_OWN,
    },
    [EntityType.BOARD]: {
      all: Permission.BOARD_CREATE,
      own: Permission.BOARD_CREATE_OWN,
    },
    [EntityType.MATCH]: {
      all: Permission.MATCHES_CREATE,
    },
  };

  const permissions = createPermissionMap[entityType];

  // Check if user has full create permission
  if (hasPermission(userPermissions, permissions.all)) {
    return true;
  }

  // Check if user can create their own entity
  if (
    permissions.own &&
    userId &&
    currentUserId &&
    userId === currentUserId &&
    hasPermission(userPermissions, permissions.own)
  ) {
    return true;
  }

  return false;
}

/**
 * Check if user can create content (as draft or published)
 *
 * Note: With the new inline draft system, all content editors can create content.
 * Whether it's published immediately depends on their publishing permissions.
 */
export function canCreateDraft(
  userPermissions: UserPermissions | null | undefined
): boolean {
  if (!userPermissions) return false;

  return (
    hasPermission(userPermissions, Permission.CONTENT_CREATE_PUBLISHED) ||
    isAdmin(userPermissions)
  );
}

/**
 * Check if user can create and publish content directly
 */
export function canCreatePublished(
  userPermissions: UserPermissions | null | undefined
): boolean {
  if (!userPermissions) return false;

  return (
    hasPermission(userPermissions, Permission.CONTENT_CREATE_PUBLISHED) ||
    isAdmin(userPermissions)
  );
}

// ============================================================================
// ENTITY DELETION PERMISSIONS
// ============================================================================

/**
 * Check if user can delete an entity
 */
export function canDelete(
  userPermissions: UserPermissions | null | undefined,
  entityType: EntityType,
  entityOwnerId?: string,
  currentUserId?: string
): boolean {
  if (!userPermissions) return false;

  // Admin can always delete
  if (isAdmin(userPermissions)) return true;

  const deletePermissionMap: Record<EntityType, { all: Permission; own?: Permission }> = {
    [EntityType.USER]: {
      all: Permission.USERS_DELETE,
    },
    [EntityType.CONTENT]: {
      all: Permission.CONTENT_DELETE_ALL,
      own: Permission.CONTENT_DELETE_OWN,
    },
    [EntityType.TEAM]: {
      all: Permission.TEAMS_DELETE,
    },
    [EntityType.SPONSOR]: {
      all: Permission.SPONSORS_DELETE,
    },
    [EntityType.TRAINER]: {
      all: Permission.TRAINERS_DELETE,
      own: Permission.TRAINERS_DELETE_OWN,
    },
    [EntityType.BOARD]: {
      all: Permission.BOARD_DELETE,
      own: Permission.BOARD_DELETE_OWN,
    },
    [EntityType.MATCH]: {
      all: Permission.MATCHES_DELETE,
    },
  };

  const permissions = deletePermissionMap[entityType];

  // Check if user has full delete permission
  if (hasPermission(userPermissions, permissions.all)) {
    return true;
  }

  // Check if user owns the entity and has own delete permission
  if (
    permissions.own &&
    entityOwnerId &&
    currentUserId &&
    entityOwnerId === currentUserId &&
    hasPermission(userPermissions, permissions.own)
  ) {
    return true;
  }

  return false;
}

// ============================================================================
// VIEW PERMISSIONS
// ============================================================================

/**
 * Check if user can view all entities of a type
 */
export function canViewAll(
  userPermissions: UserPermissions | null | undefined,
  entityType: EntityType
): boolean {
  if (!userPermissions) return false;

  // Admin can view all
  if (isAdmin(userPermissions)) return true;

  const viewAllPermissionMap: Record<EntityType, Permission> = {
    [EntityType.USER]: Permission.USERS_VIEW_ALL,
    [EntityType.CONTENT]: Permission.CONTENT_VIEW_ALL,
    [EntityType.TEAM]: Permission.TEAMS_VIEW_ALL,
    [EntityType.SPONSOR]: Permission.SPONSORS_VIEW_ALL,
    [EntityType.TRAINER]: Permission.TRAINERS_VIEW_ALL,
    [EntityType.BOARD]: Permission.BOARD_VIEW_ALL,
    [EntityType.MATCH]: Permission.MATCHES_VIEW_ALL,
  };

  return hasPermission(userPermissions, viewAllPermissionMap[entityType]);
}

/**
 * Check if user can view own entities of a type
 */
export function canViewOwn(
  userPermissions: UserPermissions | null | undefined,
  entityType: EntityType
): boolean {
  if (!userPermissions) return false;

  const viewOwnPermissionMap: Partial<Record<EntityType, Permission>> = {
    [EntityType.USER]: Permission.USERS_VIEW_OWN,
    [EntityType.CONTENT]: Permission.CONTENT_VIEW_OWN,
    [EntityType.TEAM]: Permission.TEAMS_VIEW_OWN,
  };

  const permission = viewOwnPermissionMap[entityType];
  if (!permission) return false;

  return hasPermission(userPermissions, permission) || canViewAll(userPermissions, entityType);
}

// ============================================================================
// DASHBOARD ACCESS PERMISSIONS
// ============================================================================

export type DashboardSection = 'users' | 'content' | 'teams' | 'sponsors' | 'matches';

/**
 * Check if user can access a specific dashboard section
 */
export function canAccessDashboard(
  userPermissions: UserPermissions | null | undefined,
  section: DashboardSection
): boolean {
  if (!userPermissions) return false;

  // Admin can access all sections
  if (isAdmin(userPermissions)) return true;

  const dashboardPermissionMap: Record<DashboardSection, Permission> = {
    users: Permission.ACCESS_DASHBOARD_USERS,
    content: Permission.ACCESS_DASHBOARD_CONTENT,
    teams: Permission.ACCESS_DASHBOARD_TEAMS,
    sponsors: Permission.ACCESS_DASHBOARD_SPONSORS,
    matches: Permission.ACCESS_DASHBOARD_MATCHES,
  };

  return hasPermission(userPermissions, dashboardPermissionMap[section]);
}

// ============================================================================
// DRAFT UTILITIES
// ============================================================================

/**
 * Get the draft table name for an entity type
 */
export function getDraftTableName(entityType: EntityType): string {
  const tableMap: Record<EntityType, string> = {
    [EntityType.USER]: 'profile_drafts',
    [EntityType.CONTENT]: 'news_drafts',
    [EntityType.TEAM]: 'team_drafts',
    [EntityType.SPONSOR]: 'sponsor_drafts',
    [EntityType.TRAINER]: 'trainer_drafts',
    [EntityType.BOARD]: 'boardmember_drafts',
    [EntityType.MATCH]: 'matches', // no separate draft table, matches use the published flag
  };

  return tableMap[entityType];
}

/**
 * Get the entity ID field name for a draft table
 */
export function getEntityIdFieldName(entityType: EntityType): string {
  const fieldMap: Record<EntityType, string> = {
    [EntityType.USER]: 'profile_id',
    [EntityType.CONTENT]: 'news_id',
    [EntityType.TEAM]: 'team_id',
    [EntityType.SPONSOR]: 'sponsor_id',
    [EntityType.TRAINER]: 'trainer_id',
    [EntityType.BOARD]: 'boardmember_id',
    [EntityType.MATCH]: 'match_id',
  };

  return fieldMap[entityType];
}

/**
 * Compare two objects and return list of changed fields
 */
export function getChangedFields<T extends Record<string, any>>(
  original: T,
  updated: T
): string[] {
  const changed: string[] = [];

  // Check all keys in updated object
  for (const key in updated) {
    if (JSON.stringify(original[key]) !== JSON.stringify(updated[key])) {
      changed.push(key);
    }
  }

  return changed;
}

/**
 * Extract only the changed fields from an updated object
 */
export function extractChangedData<T extends Record<string, any>>(
  original: T,
  updated: T
): Partial<T> {
  const changed: Partial<T> = {};

  for (const key in updated) {
    if (JSON.stringify(original[key]) !== JSON.stringify(updated[key])) {
      changed[key as keyof T] = updated[key];
    }
  }

  return changed;
}

// ============================================================================
// TEAM-SPECIFIC PERMISSIONS
// ============================================================================

/**
 * Check if user can edit teams (and therefore view draft data)
 * Users with any team editing permission should be able to see draft changes
 */
export function canEditAnyTeam(
  userPermissions: UserPermissions | null | undefined
): boolean {
  if (!userPermissions) return false;

  // Admin can always edit
  if (isAdmin(userPermissions)) return true;

  // Check if user has any team editing permissions (UPDATE_ALL or UPDATE_OWN)
  return hasAnyPermission(userPermissions, [
    Permission.TEAMS_UPDATE_ALL,
    Permission.TEAMS_UPDATE_OWN,
  ]);
}

// ============================================================================
// MATCH-SPECIFIC PERMISSIONS
// ============================================================================

/**
 * Mirrors the SQL function can_edit_match(team_id):
 * MATCHES_UPDATE_ALL, or MATCHES_UPDATE_OWN for a team the user trains.
 */
export function canEditMatch(
  userPermissions: UserPermissions | null | undefined,
  teamId: number,
  trainerTeamIds: number[]
): boolean {
  if (!userPermissions) return false;
  if (isAdmin(userPermissions)) return true;
  if (hasPermission(userPermissions, Permission.MATCHES_UPDATE_ALL)) return true;
  return hasPermission(userPermissions, Permission.MATCHES_UPDATE_OWN) && trainerTeamIds.includes(teamId);
}

/**
 * Whether the user can edit matches of any team (dashboard shows all teams)
 */
export function canEditAllMatches(userPermissions: UserPermissions | null | undefined): boolean {
  return isAdmin(userPermissions) || hasPermission(userPermissions, Permission.MATCHES_UPDATE_ALL);
}
